/**
 * KarroFin Admin — build configuration.
 *
 * USE_MOCK is the single switch between demo data and live endpoints. Every
 * function in api/ checks it; nothing else in the module knows about mocking.
 *
 * It is ON because the dev3 database is empty by design (0 products,
 * 0 offers, 0 loans), so live endpoints would render every screen as an empty
 * state. Flip to false module-by-module as real endpoints are verified.
 */
export const USE_MOCK = true;

/**
 * Modules verified against live endpoints. A module listed here ignores
 * USE_MOCK and calls the real API; everything else stays on mock data.
 *
 * `products` went live once the dev3 database was seeded (see
 * scripts/seed-products.sh) -- the product logo has to attach to a real row,
 * so it could not work against mocks. The other screens have no real data
 * behind them yet, so flipping them now would just empty them out.
 */
export const LIVE_MODULES = {
  products: true,
  // Switched on once the database had customers and loans to show. Before
  // that these screens showed mock rows while their "Download report" read the
  // real database -- so the screen looked full and the spreadsheet came back
  // with nothing but headers. One source now, so the two always agree.
  users: true,
  applications: true,
  // Switched on once the three karrofin_notification_* endpoints existed and
  // scripts/seed-notifications.sh had put rows behind them. Note this makes the
  // screen's READS live only -- sending is a separate switch (ALLOW_REAL_SEND
  // below) and stays off, because dev3's credentials are production ones.
  notifications: true,
  // Switched on once web-panel/karrofin_dashboard/ existed. Its numbers are
  // counts over real offers and customers, so unlike the other screens it does
  // not need seeded rows to be worth looking at -- an empty range renders "-"
  // on the ratios rather than a misleading 0%.
  dashboard: true,
  // The Automation screen's Notification History. Live from the start: it is
  // an audit of what the engine really logged, and mock rows on an audit
  // screen would read as messages that never existed.
  automation_history: true,
  // The Agent Tracker. Live only, for the same reason: it reports what the
  // growth agent really planned and sent, and has no mock data at all.
  growth_tracker: true,
};

/** True when `module` should still use mock data. */
export function useMock(module) {
  return USE_MOCK && !LIVE_MODULES[module];
}

/**
 * Simulated latency for mock calls, in milliseconds. Keeping this non-zero is
 * deliberate: it exercises the loading skeletons on every screen instead of
 * letting them flash past, which is how the empty/loading/error states got
 * tested at all.
 */
export const MOCK_LATENCY_MS = 320;

/** Rows per page. Matches the legacy default in app-js/keys.js (PAGE_LENGTH). */
export const PAGE_LENGTH = 10;

/**
 * Notification sending is real.
 *
 * It was preview-only for a long time, and for a good reason: dev3's .env was
 * copied from production, so WHATSAPP_AUTH_KEY, the MSG91 SMS credentials and
 * the Firebase service account are all LIVE, and a send from here reaches real
 * handsets.
 *
 * That reason has not gone away -- it has moved somewhere it can actually be
 * enforced. The guard is now server-side: settings.NOTIFICATION_TEST_RECIPIENTS
 * is an allowlist every recipient is checked against before dispatch, and an
 * unconfigured box (empty list, NOTIFICATION_ALLOW_ALL_RECIPIENTS off) sends to
 * nobody. A browser flag could never be that guard: it protects only the people
 * who go through this screen, and anything calling the API directly walks past
 * it.
 */
export const ALLOW_REAL_SEND = true;


/** Default date range for every screen's filter bar, in days. */
export const DEFAULT_RANGE_DAYS = 30;
